import { useLocation } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { Suspense } from 'react';

import { LoadingScreen } from '@components/feedback/LoadingScreen';
import { CTASection } from '@components/sections/CTASection';
import useAnimationPreferences from '@hooks/use-animation-preferences';
import { useLocale } from '@hooks/use-locale';
import Footer from './Footer/Footer';
import Header from './Header/Header';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { locale } = useLocale();
  const isTamil = locale === 'ta';

  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col">
      <Header />
      <Suspense
        fallback={
          <main id="main-content" className="bg-background min-h-screen grow pt-16 md:pt-20" tabIndex={-1}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center py-16"
              aria-label={isTamil ? 'உள்ளடக்கம் ஏற்றப்படுகிறது' : 'Loading content'}
              role="status"
            >
              <LoadingScreen
                message={
                  isTamil ? 'உங்கள் அடுத்த பட்டு அத்தியாயம் நெய்யப்படுகிறது...' : 'Weaving your next silk chapter...'
                }
              />
            </motion.div>
          </main>
        }
      >
        <PageChrome pathname={location.pathname}>{children}</PageChrome>
      </Suspense>
    </div>
  );
}

function PageChrome({ children, pathname }: LayoutProps & { pathname: string }) {
  const { shouldAnimate } = useAnimationPreferences();
  const basePathname = pathname.replace(/^\/ta(?=\/|$)/, '') || '/';
  const shouldShowGlobalCta = !['/contact', '/search'].includes(basePathname) && !basePathname.startsWith('/account');

  return (
    <>
      <motion.div
        key={`${pathname}-zari-transition`}
        className="pointer-events-none fixed inset-x-0 top-0 z-90 h-0.5 origin-left bg-[linear-gradient(90deg,transparent,#d9a227_18%,#f3d486_50%,#d9a227_82%,transparent)] shadow-[0_0_18px_rgba(217,162,39,0.5)]"
        initial={false}
        animate={shouldAnimate ? { scaleX: [0, 1, 0], originX: [0, 0, 1] } : { opacity: 0 }}
        transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden="true"
      />
      <AnimatePresence initial={false} mode="sync">
        <motion.main
          id="main-content"
          key={pathname}
          initial={false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, y: shouldAnimate ? -4 : 0 }}
          transition={{
            duration: 0.24,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="bg-background min-h-screen grow pt-16 md:pt-20"
          tabIndex={-1} // allows focus but excludes from tab order
        >
          {children}
        </motion.main>
      </AnimatePresence>
      {shouldShowGlobalCta ? <CTASection /> : null}
      <Footer />
    </>
  );
}
